import React, { useState, useEffect, useMemo, useRef } from "react";
import { FiChevronDown } from "react-icons/fi";
import { FaFilter } from "react-icons/fa6";
import Header from "../Components/Header.js";
import Sidebar from "../Components/Sidebar.js";
import "../assets/css/Dashboard.css";
import DataSourcePanel from "../Components/DataSourcePanel.js";
import QuickAnalysis from "../Components/QuickAnalysis.js";
import {
  getHotSpotKebele,
  getHotSpotZone,
  getHotSpotWoreda,
  getHotSpotRegionalState,
} from "../Services/apiService";
import { useLocation } from "react-router-dom";
import { IoCloseCircleOutline } from "react-icons/io5";

export default function Dashboard() {
  const location = useLocation();
  const locationName = location.state?.locationName || "Ethiopia";
  const [key, value] = locationName.split(":");
  const [error, setError] = useState(null);
  const [alertView, setAlertView] = useState("disease-hotspot");
  const [loadingHotspot, setLoadingHotspot] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;
  const [sortField, setSortField] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");
  const [selectedLevel, setSelectedLevel] = useState("regionalState");
  const [region, setRegion] = useState("Ethiopia");
  const [regionalStates, setRegionalStates] = useState([]);
  const [zones, setZones] = useState([]);
  const [woredas, setWoredas] = useState([]);
  const [kebeles, setKebeles] = useState([]);
  const [allRegionalStates, setAllRegionalStates] = useState([]);
  const [allZones, setAllZones] = useState([]);
  const [allWoredas, setAllWoredas] = useState([]);
  const [allKebeles, setAllKebeles] = useState([]);
  const [previousRegionalState, setPreviousRegionalState] = useState("");
  const [previousZone, setPreviousZone] = useState("");
  const [previousWoreda, setPreviousWoreda] = useState("");
  const [selectedFilter, setSelectedFilter] = useState("");
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [refreshDashboard, setRefreshDashboard] = useState(0);
  const normalize = (str) =>
    (str || "")
      .replace(/\s+/g, " ")
      .replace(/–|—/g, "-")
      .trim()
      .toLowerCase();
  const alertToEndemicityMap = {
    High: [
      ">=50% prevalence (high)",
    ],
    Medium: [
      "10 - 49.9% prevalence (moderate)",
      "10 - 49.9% post-IA prevalence",
    ],
    Low: [
      "1 - 9.9% prevalence (low)",
      "1 - 9.9% post-IA prevalence",
      "<1% prevalence (non-endemic)",
      "Surveillance (prevalence <1%)",
    ],
    "No Data": ["no-data", "No Data"],
  };
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const filterOptions = [
    {
      value: "1 - 9.9% post-IA prevalence",
      label: "1 - 9.9% post-IA prevalence",
    },
    {
      value: "<1% prevalence (non-endemic)",
      label: "<1% prevalence (non-endemic)",
    },
    {
      value: "10 - 49.9% post-IA prevalence",
      label: "10 - 49.9% post-IA prevalence",
    },
    { value: "1 - 9.9% prevalence (low)", label: "1 - 9.9% prevalence (low)" },
    {
      value: "Surveillance (prevalence <1%)",
      label: "Surveillance (prevalence <1%)",
    },
    {
      value: "10 - 49.9% prevalence (moderate)",
      label: "10 - 49.9% prevalence (moderate)",
    },
    { value: ">=50% prevalence (high)", label: ">=50% prevalence (high)" },
    { value: "no-data", label: "No Data" },
  ];

  const handleFilterSelect = (value) => {
    setSelectedFilter(value);
    setIsDropdownOpen(false);
  };
  const closeFilter = () => {
    setIsDropdownOpen(false);
  };
  const clearFilter = () => {
    setSelectedFilter("");
  };
  const clearAlert = () => {
    setSelectedAlert("");
  };
  const [initialKey] = locationName.split(":");
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedLevel]);
  const filterDataByEndemicity = (data) => {
    if (!selectedFilter) return data;

    return data.filter((item) => {
      const endemic = normalize(item.endemicity);
      const filter = normalize(selectedFilter);
      if (filter === "no-data") {
        return endemic === "" || endemic === "no data";
      }
      return endemic === filter;
    });
  };
  const filterDataByAlertLevel = (data) => {
    if (!selectedAlert) return data;

    const allowedValues = alertToEndemicityMap[selectedAlert] || [];
    return data.filter((item) => {
      const endemic = normalize(item.endemicity);
      return allowedValues.map(normalize).includes(endemic);
    });
  };
  useEffect(() => {
    if (alertView !== "disease-hotspot") return;
    const fetchHotspotData = async () => {
      setLoadingHotspot(true);
      try {
        const [regionalData, zoneData, woredaData, kebeleData] =
          await Promise.all([
            getHotSpotRegionalState(),
            getHotSpotZone(),
            getHotSpotWoreda(),
            getHotSpotKebele(),
          ]);
        setAllRegionalStates(regionalData);
        setAllZones(zoneData);
        setAllWoredas(woredaData);
        setAllKebeles(kebeleData);
        let initialLevel = "regionalState";
        let initialData = regionalData;

        if (key === "regionalState") {
          initialLevel = "regionalState";
          initialData = regionalData.filter(
            (r) =>
              r.regionalState?.trim().toLowerCase() ===
              value?.trim().toLowerCase()
          );
          setRegion(value);
        } else if (key === "zone") {
          initialLevel = "zone";
          initialData = zoneData.filter(
            (z) =>
              z.zone?.trim().toLowerCase() === value?.trim().toLowerCase()
          );
          setRegion(value);
        } else if (key === "woreda") {
          initialLevel = "woreda";
          initialData = woredaData.filter(
            (w) =>
              w.woreda?.trim().toLowerCase() === value?.trim().toLowerCase()
          );
          setRegion(value);
        } else if (key === "kebele") {
          initialLevel = "kebele";
          initialData = kebeleData.filter(
            (k) =>
              k.kebele?.trim().toLowerCase() === value?.trim().toLowerCase()
          );
          setRegion(value);
        }
        setSelectedLevel(initialLevel);
        if (initialLevel === "regionalState") setRegionalStates(initialData);
        if (initialLevel === "zone") setZones(initialData);
        if (initialLevel === "woreda") setWoredas(initialData);
        if (initialLevel === "kebele") setKebeles(initialData);
      } catch (error) {
        console.error("Error fetching hotspot data:", error);
        setError("Failed to load hotspot data");
      } finally {
        setLoadingHotspot(false);
      }
    };

    fetchHotspotData();
  }, [alertView, locationName, refreshDashboard]);

  useEffect(() => {
    if (alertView === "disease-hotspot") {
      setSelectedLevel("regionalState");
    }
  }, [alertView]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
    setCurrentPage(1);
  };
  let diseasehotspotsConfig = { title: "", columns: [], rows: [] };

  if (selectedLevel === "regionalState") {
    diseasehotspotsConfig = {
      title: `Disease Hotspots by Regional State`,
      columns: [
        { key: "regionalState", label: "Regional State", sortable: true },
        { key: "capitalCity", label: "Capital City", sortable: true },
        { key: "endemicity", label: "Endemicity", sortable: true },
      ],
      rows: regionalStates.map((r) => ({
        regionalState: r.regionalState || "N/A",
        capitalCity: r.capitalCity || "N/A",
        endemicity: r.endemicity || "No data",
      })),
    };
  } else if (selectedLevel === "zone") {
    diseasehotspotsConfig = {
      title: `Disease Hotspots in ${region} (Zones)`,
      columns: [
        { key: "zone", label: "Zone", sortable: true },
        { key: "regionalState", label: "Regional State", sortable: true },
        { key: "endemicity", label: "Endemicity", sortable: true },
      ],
      rows: zones.map((z) => ({
        zone: z.zone || "N/A",
        regionalState: z.regionalState || "N/A",
        endemicity: z.endemicity || "No data",
      })),
    };
  } else if (selectedLevel === "woreda") {
    diseasehotspotsConfig = {
      title: `Disease Hotspots in ${region} (Woredas)`,
      columns: [
        { key: "woreda", label: "Woreda", sortable: true },
        { key: "zone", label: "Zone", sortable: true },
        { key: "regionalState", label: "Regional State", sortable: true },
        { key: "endemicity", label: "Endemicity", sortable: true },
      ],
      rows: woredas.map((w) => ({
        woreda: w.woreda || "N/A",
        zone: w.zone || "N/A",
        regionalState: w.regionalState || "N/A",
        endemicity: w.endemicity || "No data",
      })),
    };
  } else if (selectedLevel === "kebele") {
    diseasehotspotsConfig = {
      title: `Disease Hotspots in ${region} (Kebeles)`,
      columns: [
        { key: "kebele", label: "Kebele", sortable: true },
        { key: "woreda", label: "Woreda", sortable: true },
        { key: "zone", label: "Zone", sortable: true },
        { key: "regionalState", label: "Regional State", sortable: true },
        { key: "endemicity", label: "Endemicity", sortable: true },
      ],
      rows: kebeles.map((k) => ({
        kebele: k.kebele || "N/A",
        woreda: k.woreda || "N/A",
        zone: k.zone || "N/A",
        regionalState: k.regionalState || "N/A",
        endemicity: k.endemicity || "No data",
      })),
    };
  }
  const handleAlertChange = (value) => {
    setSelectedAlert(value);
  };
  let primaryKey;
  const sortedDiseaseHotspots = useMemo(() => {
    if (!diseasehotspotsConfig.rows) return [];
    const primaryKeyMap = {
      regionalState: "regionalState",
      zone: "zone",
      woreda: "woreda",
      kebele: "kebele"
    };
    primaryKey = primaryKeyMap[selectedLevel];
    let filteredRows = diseasehotspotsConfig.rows.filter(
      (row) =>
        row[primaryKey] &&
        row[primaryKey].trim() !== "" &&
        row[primaryKey] !== "N/A"
    );
    if (selectedFilter) {
      filteredRows = filterDataByEndemicity(filteredRows);
    }
    if (selectedAlert) {
      filteredRows = filterDataByAlertLevel(filteredRows);
    }
    return filteredRows.sort((a, b) => {
      const dir = sortDirection === "asc" ? 1 : -1;
      if (
        sortField === "kebele" ||
        sortField === "woreda" ||
        sortField === "zone" ||
        sortField === "regionalState"
      ) {
        return (a[sortField] || "").localeCompare(b[sortField] || "") * dir;
      }
      if (sortField === "endemicity") {
        const endemicityOrder = {
          "No data": 0,
          Low: 1,
          Moderate: 2,
          High: 3,
          "Very High": 4,
        };
        const aValue = a.endemicity || "No data";
        const bValue = b.endemicity || "No data";
        return (endemicityOrder[aValue] - endemicityOrder[bValue]) * dir;
      }
      return 0;
    });
  }, [diseasehotspotsConfig.rows, sortField, sortDirection, selectedFilter]);

  const totalPages = Math.ceil(
    (sortedDiseaseHotspots.length || 0) / rowsPerPage
  );
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedRows = sortedDiseaseHotspots
    .map((row, index) => ({
      ...row,
      srNo: index + 1,
    }))
    .slice(startIndex, endIndex);

  const finalTable =
    alertView === "disease-hotspot"
      ? diseasehotspotsConfig
      : diseasehotspotsConfig;

  const {
    title: tableTitle,
    columns: tableColumns,
    rows: tableRows,
  } = finalTable;

  const handleRowClick = (row) => {
    if (selectedLevel === "regionalState") {
      const zonesInRegion = allZones.filter(
        (data) => data.regionalState === row.regionalState
      );
      setZones(zonesInRegion);
      setSelectedLevel("zone");
      setRegion(row.regionalState);
    } else if (selectedLevel === "zone") {
      const woredasInZone = allWoredas.filter(
        (data) => data.zone === row.zone
      );
      setWoredas(woredasInZone);
      setSelectedLevel("woreda");
      setRegion(row.zone);
    } else if (selectedLevel === "woreda") {
      const kebelesInWoreda = allKebeles.filter(
        (data) => data.woreda === row.woreda
      );
      setKebeles(kebelesInWoreda);
      setSelectedLevel("kebele");
      setRegion(row.woreda);
    }
  };

  useEffect(() => {
  const rows = diseasehotspotsConfig.rows;
  if (selectedLevel === "regionalState") {
    setRegion("Ethiopia");
    return;
  }
  if (selectedLevel === "zone") {
    setRegion(rows?.[0]?.regionalState || region);
    return;
  }
  if (selectedLevel === "woreda") {
    setRegion(rows?.[0]?.zone || region);
    return;
  }
  if (selectedLevel === "kebele") {
    if (rows && rows.length > 0) {
      setRegion(rows[0].woreda);
    } else {
      setRegion(region); 
    }
    return;
  }
}, [selectedLevel, diseasehotspotsConfig.rows]);

  const getAlertCategory = (endemicity = "") => {
    const cleanValue = endemicity.trim().toLowerCase();
    for (const category in alertToEndemicityMap) {
      const match = alertToEndemicityMap[category]
        .map(item => item.trim().toLowerCase())
        .includes(cleanValue);
      if (match) return category;
    }
    return "No Data";
  };
  const endemicityCounts = diseasehotspotsConfig.rows.reduce(
    (counts, row) => {
      const category = getAlertCategory(row.endemicity || "No Data");
      counts[category]++;
      return counts;
    },
    { High: null, Medium: null, Low: null, "No Data": null }
  );

  return (
    <div className="dashboard-container">
      <Header />
      <div className="dashboard-grid">
        <Sidebar onSelect={setAlertView} alertView={alertView} />{" "}
        <section className="center-content">
          <QuickAnalysis
            alertView={true}
            handleAlertChange={handleAlertChange}
            selectedAlert={selectedAlert}
            clearAlert={clearAlert}
            endemicityCounts={endemicityCounts}
            sortedDiseaseHotspots={sortedDiseaseHotspots}
          />
          <div className="alertsContainer">
            {!loadingHotspot && (
              <div className="alertsHeader">
                <div className="alertsTitle">
                  <span className="alertsTitleHighlight">{tableTitle}</span>
                </div>

                <div className="headerContainer">
                  {sortedDiseaseHotspots.length > 1 && (
                    <div className="filter-dropdown" ref={dropdownRef}>
                      <div
                        className="filter-icon-button"
                        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                        aria-haspopup="true"
                        aria-expanded={isDropdownOpen}
                      >
                        <FaFilter />
                      </div>
                      {isDropdownOpen && (
                        <div className="filter-options-dropdown">
                          <div className="filter-header">
                            <p>Select a filter</p>
                            <button
                              className="close-filter"
                              onClick={closeFilter}
                              aria-label="Close"
                            >
                              <IoCloseCircleOutline />
                            </button>
                          </div>
                          <div className="filter-options-list">
                            {filterOptions.map((option, index) => (
                              <div
                                key={index}
                                className={`filter-option ${selectedFilter === option.value
                                  ? "selected"
                                  : ""
                                  }`}
                                onClick={() => handleFilterSelect(option.value)}
                              >
                                {option.label}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  {selectedFilter && (
                    <button
                      className="clear-filter-dashboard"
                      onClick={clearFilter}
                    >
                      Clear
                    </button>
                  )}
                  {alertView === "disease-hotspot" && totalPages > 1 && (
                    <div className="paginationContainer">
                      <button
                        className="paginationButton"
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage((p) => p - 1)}
                      >
                        Prev
                      </button>

                      <span className="paginationInfo">
                        Page {currentPage} of {totalPages}
                      </span>

                      <button
                        className="paginationButton"
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage((p) => p + 1)}
                      >
                        Next
                      </button>
                    </div>
                  )}
                  {selectedLevel !== initialKey &&
                    selectedLevel !== "regionalState" && (
                      <button
                        className="backButton"
                        onClick={() => {
                          if (selectedLevel === "zone")
                            setSelectedLevel("regionalState");
                          else if (selectedLevel === "woreda")
                            setSelectedLevel("zone");
                          else if (selectedLevel === "kebele")
                            setSelectedLevel("woreda");
                        }}
                      >
                        ← Back
                      </button>
                    )}
                </div>
              </div>
            )}
            <div className="tableContainer">
              {loadingHotspot ? (
                <div className="table-spinner-container">
                  <div className="spinner-hotspot"></div>
                </div>
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th>
                        <button className="sortButton">
                          Sr No
                          <FiChevronDown className="sortIcon invisible" />
                        </button>
                      </th>
                      {tableColumns.map((col) => (
                        <th key={col.key}>
                          {col.sortable ? (
                            <button
                              onClick={() => handleSort(col.key)}
                              className="sortButton"
                            >
                              {col.label}
                              <FiChevronDown
                                className={`sortIcon ${sortField === col.key &&
                                  sortDirection === "desc"
                                  ? "rotated"
                                  : ""
                                  }`}
                              />
                            </button>
                          ) : (
                            col.label
                          )}
                        </th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {(alertView === "disease-hotspot"
                      ? paginatedRows
                      : tableRows
                    ).length === 0 ? (
                      <tr>
                        <td
                          colSpan={tableColumns.length + 1}
                          style={{ textAlign: "center", padding: "1rem" }}
                        >
                          {selectedLevel === "woreda"
                            ? "No Woredas available for this Zone"
                            : selectedLevel === "zone"
                              ? "No Zones available for this Region"
                              : selectedLevel === "kebele"
                                ? "No Kebeles available for this Woreda"
                                : "No data available"}
                        </td>
                      </tr>
                    ) : (
                      (alertView === "disease-hotspot"
                        ? paginatedRows
                        : tableRows
                      ).map((row, index) => {
                        const rowKey =
                          alertView === "disease-hotspot"
                            ? `${row.name}-${row.region}-${row.zone}-${row.woreda}-${index}`
                            : row.metric || `row-${index}`;

                        const srNo =
                          alertView === "disease-hotspot"
                            ? row.srNo
                            : (currentPage - 1) * rowsPerPage + index + 1;

                        return (
                          <tr
                            key={rowKey}
                            className={row.selected ? "selected" : ""}
                          >
                            <td className="srNoCell">{srNo}</td>

                            {tableColumns.map((col) => {
                              if (col.key === primaryKey && row[primaryKey]) {
                                const isClickable =
                                  selectedLevel === "regionalState" ||
                                  selectedLevel === "zone" ||
                                  selectedLevel === "woreda";
                                return (
                                  <td key={col.key}>
                                    <div className="fileCell">
                                      <span
                                        className={`fileName ${isClickable ? "clickable" : ""
                                          }`}
                                        onClick={
                                          isClickable
                                            ? () => handleRowClick(row)
                                            : undefined
                                        }
                                      >
                                        {row[primaryKey]}
                                      </span>
                                    </div>
                                  </td>
                                );
                              }

                              if (col.key === "infections" && row.infections) {
                                return (
                                  <td key={col.key} className="infectionsCell">
                                    {row.infections} infections
                                  </td>
                                );
                              }

                              if (col.key === "severity" && row.severity) {
                                return (
                                  <td key={col.key}>
                                    <span
                                      className={`severityBadge ${row.severity === "critical"
                                        ? "severityCritical"
                                        : "severityHigh"
                                        }`}
                                    >
                                      {row.severity}
                                    </span>
                                  </td>
                                );
                              }

                              return (
                                <td key={col.key}>
                                  <span className="contaminationText">
                                    {row[col.key] ?? ""}
                                  </span>
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </section>
        <DataSourcePanel onRefresh={() => setRefreshDashboard(p => p + 1)}/>
      </div>
    </div>
  );
}
